/**
 * Curved ("bezier") line path strategy.
 *
 * Draws a smooth curve from start to end that passes THROUGH the given control
 * points (a Konva.Line through `[start, ...controls, end]` with `tension`, so it
 * interpolates smoothly — handle-on-the-curve UX). Controls are supplied in the
 * group's LOCAL frame via `controlPoints` (the renderer translates
 * `LineObject.curveControls` — absolute world points — into local). With no
 * control it defaults to a single implicit control at the endpoint midpoint, so
 * a fresh curved line looks straight until the user drags/adds a control.
 *
 * The user double-clicks the line to add a control and double-clicks a control
 * to remove it — the strategy simply renders however many are present.
 */

import {
  type KonvaLineProps,
  type LinePathStrategy,
} from './line-path-strategy.interface.js';

export const CURVED_STRATEGY_ID = 'curved';

/** How tightly the curve hugs the control points (Konva tension). */
const CURVE_TENSION = 0.5;

/** The implicit default control when a curved line has none: the midpoint. */
export function curveMidpoint(
  start: { x: number; y: number },
  end: { x: number; y: number },
): { x: number; y: number } {
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
}

export class CurvedPathStrategy implements LinePathStrategy {
  readonly id: string = CURVED_STRATEGY_ID;

  computePath(
    start: { x: number; y: number },
    end: { x: number; y: number },
    controlPoints?: readonly { x: number; y: number }[],
  ): KonvaLineProps {
    const controls = controlPoints && controlPoints.length > 0
      ? controlPoints
      : [curveMidpoint(start, end)];
    const points: number[] = [start.x, start.y];
    for (const c of controls) points.push(c.x, c.y);
    points.push(end.x, end.y);
    return { points, tension: CURVE_TENSION };
  }
}
